"use client";

import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, UserPlus, Crown, AlertCircle } from "lucide-react";
import {
  UserRoles,
  userRolesTranslations,
  type OrganizationUserObj,
} from "@/interfaces";
import CustomCombobox from "@/components/CustomCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

interface MemberListProps {
  userLoggedRole: UserRoles;
  members: OrganizationUserObj[];
  onRemoveMember: (memberId: string) => void;
  onAddMember: (email: string) => void;
  onUpdateMemberRole: (memberId: string, newRole: UserRoles) => void;
  onTransferOwnership: (memberId: string) => void;
}

export function MemberList({
  userLoggedRole,
  members,
  onRemoveMember,
  onAddMember,
  onUpdateMemberRole,
  onTransferOwnership,
}: Readonly<MemberListProps>) {
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleAddMember = () => {
    if (newMemberEmail.trim() === "") {
      setEmailError("O email é obrigatório");
      return;
    }

    if (!isValidEmail(newMemberEmail)) {
      setEmailError("Email inválido");
      return;
    }

    setEmailError("");
    onAddMember(newMemberEmail);
    setNewMemberEmail("");
  };

  const isValidEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const roleOptions = Object.entries(userRolesTranslations)
    .filter(([role]) => role !== UserRoles.OWNER && role !== UserRoles.ADMIN)
    .map(([value, label]) => ({
      value,
      label,
    }));

  const isOwner = userLoggedRole === UserRoles.OWNER;
  const isAdmin = userLoggedRole === UserRoles.ADMIN;
  const canEdit = isOwner || isAdmin;

  const getRoleBadgeColor = (role: UserRoles) => {
    switch (role) {
      case UserRoles.OWNER:
        return "bg-amber-100 text-amber-800 border-amber-200";
      case UserRoles.ADMIN:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case UserRoles.MANAGER:
        return "bg-purple-100 text-purple-800 border-purple-200";
      case UserRoles.MEMBER:
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="w-full mt-4">
      <Card className="mb-6 border border-[#EE7B11]/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4">
            {canEdit && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Input
                    type="email"
                    placeholder="Email do novo membro"
                    value={newMemberEmail}
                    onChange={(e) => {
                      setNewMemberEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    className={`w-full sm:w-64 ${
                      emailError ? "border-red-500" : ""
                    }`}
                  />
                  {emailError && (
                    <div className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {emailError}
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleAddMember}
                  className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full sm:w-auto"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Convidar
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="hidden sm:grid grid-cols-[2fr,3fr,2fr,auto] gap-4 mb-2 font-semibold text-gray-600 px-4">
        <div>Nome</div>
        <div>Email</div>
        <div>Função</div>
        {canEdit && <div className="text-right">Ações</div>}
      </div>

      {members.length > 0 ? (
        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.user?.id}
              className="flex flex-col sm:grid sm:grid-cols-[2fr,3fr,2fr,auto] gap-2 sm:gap-4 items-start sm:items-center p-4 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors"
            >
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <Avatar>
                  <AvatarImage
                    src={
                      member.user?.picture ||
                      `https://api.dicebear.com/6.x/initials/svg?seed=${
                        member.user?.name || "/placeholder.svg"
                      }`
                    }
                    alt={member.user?.name}
                  />
                  <AvatarFallback>
                    {(member.user?.name || "U")[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="font-medium text-gray-900">
                          {member.user?.name || "Usuário"}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{member.user?.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <p
                    className="text-sm text-gray-500 sm:hidden"
                    title={member.user?.email}
                  >
                    {member.user?.email}
                  </p>
                </div>
              </div>

              <p
                className="hidden sm:block text-sm text-gray-600 truncate"
                title={member.user?.email}
              >
                {member.user?.email}
              </p>

              <div className="w-full sm:w-full">
                {isOwner && member.role !== UserRoles.OWNER ? (
                  <CustomCombobox
                    className="w-full sm:w-[135px]"
                    options={roleOptions}
                    placeholder="Selecione a permissão"
                    onChange={(value) =>
                      onUpdateMemberRole(
                        member.user?.userId,
                        UserRoles[value as keyof typeof UserRoles]
                      )
                    }
                    value={member.role}
                  />
                ) : isAdmin &&
                  ![UserRoles.OWNER, UserRoles.ADMIN].includes(member.role) ? (
                  <CustomCombobox
                    className="w-full sm:w-[135px]"
                    options={roleOptions}
                    placeholder="Selecione a permissão"
                    onChange={(value) =>
                      onUpdateMemberRole(
                        member.user?.userId,
                        UserRoles[value as keyof typeof UserRoles]
                      )
                    }
                    value={member.role}
                  />
                ) : (
                  <Badge
                    className={`font-medium inline-flex items-center ${getRoleBadgeColor(
                      member.role
                    )}`}
                    variant="outline"
                  >
                    {userRolesTranslations[member.role]}
                  </Badge>
                )}
              </div>

              <div className="flex justify-end w-full sm:w-auto mt-2 sm:mt-0 sm:ml-auto">
                {isOwner && member.role === UserRoles.ADMIN && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="p-0 h-8 w-8 mr-2 border-amber-500 text-amber-500 hover:bg-amber-50"
                      >
                        <Crown className="h-4 w-4" />
                        <span className="sr-only">Transferir propriedade</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Confirmar transferência de propriedade
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja transferir a propriedade para{" "}
                          <span className="font-medium">
                            {member.user?.name}
                          </span>
                          ? Esta ação não pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            onTransferOwnership(member.user?.userId)
                          }
                          className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                        >
                          Transferir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {isOwner && member.role !== UserRoles.OWNER && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="p-0 h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remover membro</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar remoção</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja remover{" "}
                          <span className="font-medium">
                            {member.user?.name}
                          </span>{" "}
                          da organização? Esta ação não pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onRemoveMember(member.user?.userId)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Remover
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500 font-medium">Nenhum membro encontrado</p>
          <p className="text-gray-400 text-sm mt-1">
            Adicione membros usando o formulário acima
          </p>
        </div>
      )}
    </div>
  );
}
